import type { ReactNode } from 'react'
import { radius } from '../components/primitives/shape'
import { ColumnResizer, useColumnWidth } from './ColumnResizer'

/** What the right column holds. One thing at a time: opening another replaces it. */
export type LaneContent =
  /** `over`: on a phone, the thread the profile's sheet covers. */
  | { kind: 'profile'; name: string; over?: string }
  | { kind: 'thread'; rootId: string }

const LANE = { key: 'stagepost.width.lane', width: 420, min: 320, max: 640 } as const

/**
 * The column on the right of the room on a desktop, where Slack opens a thread
 * or a profile. Escape closes it, which the workspace owns, as it owns every
 * layer's Escape. A phone has no column: what it would hold is a sheet or a
 * screen there.
 */
export function Lane({ title, subtitle, onClose, children }: {
  title: string
  subtitle?: ReactNode
  onClose: () => void
  children: ReactNode
}) {
  const column = useColumnWidth(LANE.key, LANE.width, LANE.min, LANE.max)
  return (
    <aside
      aria-label={title}
      style={{
        position: 'relative', flex: 'none', width: column.width, display: 'flex', flexDirection: 'column',
        background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: radius.lg,
      }}
    >
      <ColumnResizer column={column} label="Resize the right column" edge="left" />
      <header
        style={{
          display: 'flex', alignItems: 'baseline', gap: 8, flex: 'none',
          padding: '16px 14px 14px 18px', borderBottom: '1px solid var(--border)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{title}</h2>
        {subtitle && <span style={{ fontSize: 12.5, color: 'var(--text-meta)' }}>{subtitle}</span>}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close ${title.toLowerCase()}`}
          className="sp-tap"
          style={{
            alignSelf: 'center', display: 'grid', placeItems: 'center', width: 28, height: 28, borderRadius: radius.xs, flex: 'none',
            cursor: 'pointer', color: 'var(--text-meta)',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="m3 3 6 6M9 3 3 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </header>
      <div className="sp-scroller" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehaviorY: 'contain' }}>
        {children}
      </div>
    </aside>
  )
}
